import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { movePotToPrimary } from '../api/potsApi';
import { routeBuilders } from '../../../routes';
import type { MoveToPrimaryResponse } from '../types/potMove.types';

interface MoveToPrimaryParams {
  potId: string;
}

/**
 * Mutation hook for promoting a non-primary pot to primary, or merging it into the existing primary.
 *
 * Errors are handled by the global QueryClient onError (shows snackbar).
 * Use `createMoveToPrimaryHandler` to get a ready-made callback with navigation + snackbar.
 */
export function useMoveToPrimary() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ potId }: MoveToPrimaryParams) => {
      return await movePotToPrimary(potId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pots'] });
      queryClient.invalidateQueries({ queryKey: ['potDetails'] });
      queryClient.invalidateQueries({ queryKey: ['potPeriods'] });
    },
  });
}

/**
 * Creates a move-to-primary handler with navigation and snackbar wired in.
 * Shared between PotApprovalsPage and PotDetailsPage.
 */
export function useMoveToPrimaryHandler(
  setSnackbar: (value: {
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }) => void
) {
  const navigate = useNavigate();
  const { mutate } = useMoveToPrimary();

  return useCallback(
    (potId: string) => {
      mutate(
        { potId },
        {
          onSuccess: (response: MoveToPrimaryResponse) => {
            const targetPotId = response.targetPotId ?? response.potId;
            navigate(routeBuilders.finance.potDetail(targetPotId));
            setSnackbar({
              open: true,
              message: 'Pot moved to primary',
              severity: 'success',
            });
          },
        }
      );
    },
    [mutate, navigate, setSnackbar]
  );
}
